import { useCallback, useEffect, useState } from 'react';
import { getApiBaseUrl } from '../../../../core/config/api';

/** Hook extrait de BackupManagement.tsx : recherche utilisateurs + CRUD backups. */

interface Backup {
  filename: string;
  size?: number;
  created_at?: string;
  [key: string]: any;
}

interface User {
  id: number;
  username: string;
}

interface AlertState {
  type: 'success' | 'error';
  message: string;
}

export function useBackupManagement() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [searching, setSearching] = useState(false);
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [backupsDir, setBackupsDir] = useState('');
  const [selectedBackup, setSelectedBackup] = useState<string | null>(null);
  const [alert, setAlert] = useState<AlertState | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ filename: string; userId: number } | null>(
    null
  );

  const searchUsers = useCallback(async (term: string) => {
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const apiUrl = getApiBaseUrl(true);
      const response = await fetch(`${apiUrl}/users/with-possessions`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const termLower = term.toLowerCase();
          const filtered = (data.data as any[])
            .filter(
              (u: any) =>
                u.username.toLowerCase().includes(termLower) || u.id.toString().includes(term)
            )
            .map((u: any) => ({ id: u.id, username: u.username }))
            .slice(0, 10);
          setSearchResults(filtered);
        }
      }
    } catch (err: any) {
      console.error("Erreur lors de la recherche d'utilisateurs:", err);
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (searchTerm) searchUsers(searchTerm);
      else {
        setSearchResults([]);
        setSelectedUser(null);
      }
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [searchTerm, searchUsers]);

  const loadUserBackups = useCallback(async (userId: number) => {
    setLoadingBackups(true);
    setBackups([]);
    setBackupsDir('');
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const apiUrl = getApiBaseUrl(true);
      const response = await fetch(`${apiUrl}/backups/user/${userId}`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setBackups(data.backups || []);
          setBackupsDir(data.directory || '');
        } else {
          setAlert({
            type: 'error',
            message: data.error || 'Erreur lors du chargement des sauvegardes',
          });
        }
      } else {
        const errorData = await response.json();
        setAlert({
          type: 'error',
          message: errorData.error || 'Erreur lors du chargement des sauvegardes',
        });
      }
    } catch (err: any) {
      console.error('Erreur lors du chargement des sauvegardes:', err);
      setAlert({ type: 'error', message: 'Erreur lors du chargement des sauvegardes' });
    } finally {
      setLoadingBackups(false);
    }
  }, []);

  const deleteBackup = useCallback((userId: number, filename: string) => {
    setDeleteConfirm({ filename, userId });
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!deleteConfirm) return;
    const { userId, filename } = deleteConfirm;
    setDeleteConfirm(null);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const apiUrl = getApiBaseUrl(true);
      const response = await fetch(
        `${apiUrl}/backups/user/${userId}/file/${encodeURIComponent(filename)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAlert({ type: 'success', message: 'Sauvegarde supprimée avec succès' });
          if (selectedUser) loadUserBackups(selectedUser.id);
          setSelectedBackup(null);
        } else {
          setAlert({ type: 'error', message: data.error || 'Erreur lors de la suppression' });
        }
      } else {
        const errorData = await response.json();
        setAlert({ type: 'error', message: errorData.error || 'Erreur lors de la suppression' });
      }
    } catch (err: any) {
      console.error('Erreur lors de la suppression:', err);
      setAlert({ type: 'error', message: 'Erreur lors de la suppression' });
    }
  }, [selectedUser, loadUserBackups, deleteConfirm]);

  const downloadBackup = useCallback(async (userId: number, filename: string) => {
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('auth_token');
      const apiUrl = getApiBaseUrl(true);
      const response = await fetch(
        `${apiUrl}/backups/user/${userId}/file/${encodeURIComponent(filename)}`,
        {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const blob = new Blob([JSON.stringify(data.data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          setAlert({ type: 'success', message: 'Sauvegarde téléchargée avec succès' });
        }
      } else {
        const errorData = await response.json();
        setAlert({ type: 'error', message: errorData.error || 'Erreur lors du téléchargement' });
      }
    } catch (err: any) {
      console.error('Erreur lors du téléchargement:', err);
      setAlert({ type: 'error', message: 'Erreur lors du téléchargement' });
    }
  }, []);

  useEffect(() => {
    if (alert) {
      const timer = setTimeout(() => setAlert(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [alert]);

  return {
    searchTerm,
    setSearchTerm,
    searchResults,
    setSearchResults,
    selectedUser,
    setSelectedUser,
    searching,
    backups,
    setBackups,
    loadingBackups,
    backupsDir,
    setBackupsDir,
    selectedBackup,
    setSelectedBackup,
    alert,
    setAlert,
    deleteConfirm,
    setDeleteConfirm,
    loadUserBackups,
    deleteBackup,
    confirmDelete,
    downloadBackup,
  };
}

export type UseBackupManagement = ReturnType<typeof useBackupManagement>;
